/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Thursday, 5th September 2019
 *
 * Mobx Company Store
 */
import { configure, observable, computed, action, decorate, reaction } from 'mobx';
import { firebase } from 'containers/services/Firebase';
import uuid from 'uuid';
configure({ enforceActions: 'observed' });

class CompanyStore {
  company = {};
  locations = observable([]);
  users = observable([]);
  invites = [];
  badgeHistory = observable([]);
  hasChanges = false;
  isLoading = true;
  isLocationsLoading = true;
  isUsersLoading = true;
  isInvitationsLoading = true;
  isBadgeHistoryLoading = true;
  isSendingInvite = false;

  constructor(rootStore) {
    this._stores = rootStore;

    /**
     * Waits until user is authenticated, then initializes
     * the store.  Once initialized, it will dispose itself.
     */
    this._stores.appStore.debug(`CompanyStore [listener] Waiting to init company store...`);
    reaction(
      () => this._stores.authStore.isAuth,
      isAuth => {
        this._stores.appStore.debug(`CompanyStore [reaction] Detected a change in user auth status.`);
        if (isAuth && !this._stores.authStore.isVendorUser) {
          this.initStore();
        } else {
          this._stores.appStore.debug(`CompanyStore [reaction] Detected a user signout event, resetting store.`);
          this.reset();
        }
      }
    );

    // Bind methods to (this) if used inside a promise
    this.setField = this.setField.bind(this);
    this.setArrayField = this.setArrayField.bind(this);
  }

  /**
   * Internal stores getter.  Note: NOT mobx computed
   */
  get stores() {
    return this._stores;
  }

  /**
   * The id of the current store company
   * Mobx: computed
   * @returns {string} Id of current store company.
   */
  get companyId() {
    return this.company.id;
  }

  /**
   * Checks to see if current user is a company admin
   * Mobx: computed
   * @returns {boolean}
   */
  get isAdmin() {
    if (this.stores.authStore.isAuth) {
      if (this.stores.authStore.claims.isAdmin || this.stores.authStore.hasCompanyRole(this.companyId, 'Admin')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks to see if locationId is included in this.locations array.
   * @param {string} locationId Identification number to check.
   * @returns {boolean}
   */
  locationExists(locationId) {
    let exists = false;
    this.locations.forEach(location => {
      if (location.id === locationId) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Checks to see if userId is included in this.companyUsers array.
   * @param {string} uid User identification number to check.
   * @returns {boolean}
   */
  userExists(uid) {
    let exists = false;
    this.users.forEach(user => {
      if (user.uid === uid) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Checks to see if invite id is included in this.userInvites array.
   * @param {string} id Invite identification number to check.
   * @returns {boolean}
   */
  inviteExists(id) {
    let exists = false;
    this.invites.forEach(invite => {
      if (invite.id === id) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Checks to see if invite id is included in this.userInvites array.
   * @param {string} email Invite identification number to check.
   * @returns {boolean}
   */
  inviteExistsByEmail(email) {
    let exists = false;
    this.invites.forEach(invite => {
      if (invite.email === email) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Generic action to set value of any store field.
   * @param {string} fieldName Name of store field to set.
   * @param {any} value Value of store field to set.
   * @returns {void}
   */
  setField(fieldName, value) {
    if (this[fieldName] !== undefined) {
      this[fieldName] = value;
    }
  }

  /**
   * Generic action to replace store array index value.
   * @param {array} array Store array to update
   * @param {int} index Array index to replace.
   * @param {any} newValue New array value.
   * @returns {void}
   */
  setArrayField(array, index, newValue) {
    array[index] = newValue;
  }

  /**
   * Handles the location profile form office name field updates
   * @param {Event} e HTML form event object.
   * @returns {void}
   */
  handleNameChange = e => {
    this.setField('hasChanges', true);
    this.company.name = e.target.value;
  };

  /**
   * Removed user invitation from the database
   * @function handleRemoveInvite
   * @param {Event} event HTML onClick event
   * @param {Object} rowData User invite.
   */
  handleRemoveInvite = (event, rowData) => {
    this.setField('isInvitationsLoading', true);
    const index = this.invites.indexOf(rowData);
    // Check for index length
    if (this.invites.length > 1) {
      const newValues = this.invites.splice(index, 1);
      this.setField('invites', newValues);
    } else {
      this.setField('invites', []);
    }

    // Remove Database Record
    firebase.data
      .ref('userInvites')
      .child(rowData.id)
      .remove()
      .then(() => {
        this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'User invite removed successfully.',
          'success',
          2750
        );
        this.setField('isInvitationsLoading', false);
      })
      .catch(error => {
        this.setField('isInvitationsLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Resends user invitation email
   * @function handleSendInvite
   * @param {Event} event HTML onClick event
   * @param {Object} rowData User invite.
   */
  handleSendInvite = (event, rowData) => {
    this.setField('isSendingInvite', true);
    firebase
      .sendInviteEmail({ invite: rowData })
      .then(() => {
        this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'Email sent successfully.',
          'success',
          2750
        );
        this.setField('isSendingInvite', false);
      })
      .catch(error => {
        this.setField('isSendingInvite', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Handles the location profile form onSubmit event
   * @param {Event} e HTML form onSubmit event object.
   * @returns {void}
   */
  onSubmit = e => {
    e.preventDefault();
    firebase.data
      .ref('company')
      .child(this.company.id)
      .update(this.company)
      .then(() => {
        // Success
        this.setField('hasChanges', false);
        this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'Profile updates saved successfully.',
          'success',
          2750
        );
      })
      .catch(error => {
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Initializes company store
   * Mobx: action
   * @returns {void}
   */
  initStore = () => {
    // Make sure claims is setup
    if (!this.stores.authStore.claims.hasOwnProperty('company')) {
      return;
    }
    // Valid Claims
    this.setField('isLoading', true);
    const companyId = this.stores.authStore.claims.companyId;
    this.stores.appStore.debug(`Company.initStore() [action] Fetching store values... ${companyId}`);
    firebase
      .getCompanyById(companyId)
      .then(company => {
        // Success
        this.setField('company', company);
        this.setField('isLoading', false);
        this.stores.appStore.debug('CompanyStore.initStore() [action] Store updated.', company);
        return company;
      })
      .then(company => {
        this.setLocations(company);
        this.setUsers(company);
        this.getInvites();
        this.fetchBadgeHistory();
      })
      .catch(error => {
        // Error
        this.setField('isLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Sets the companyLocations store value
   * @returns {void}
   */
  setLocations = company => {
    this.setField('isLocationsLoading', true);
    this.stores.appStore.debug('CompanyStore.setLocations() [action] Fetching locations...');
    this.getLocations(company)
      .then(locations => {
        if (locations) {
          this.setField('locations', locations.filter(value => typeof value !== 'undefined'));
          // Delay to allow mobx to catch up
          this.stores.appStore.debug('CompanyStore.getLocations() [action] Locations retrieved.', locations);
        }
        setTimeout(() => this.setField('isLocationsLoading', false), 100);
      })
      .catch(error => {
        this.setField('isLocationsLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Fetches company locations and their attributes from database
   * @param {object} company Company database object
   * @returns {void}
   */
  getLocations = async company => {
    if (company.locations) {
      const locations = Object.keys(company.locations).map(id => {
        return firebase.fetchLocationData(id).then(location => {
          return location;
        });
      });
      // Returns once all promises are resolved
      return Promise.all(locations);
    }
  };

  /**
   * Sets the users store value
   * @function setUsers
   * @returns {void}
   */
  setUsers = company => {
    this.setField('isUsersLoading', true);
    this.stores.appStore.debug('CompanyStore.setUsers() [action] Fetching users...');
    this.getUsers(company)
      .then(users => {
        this.setField('users', users.filter(value => typeof value !== 'undefined'));
        setTimeout(() => this.setField('isUsersLoading', false), 100);
        this.stores.appStore.debug('CompanyStore.getUsers() [action] Users retrieved.', users);
      })
      .catch(error => {
        this.setField('isUsersLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Fetches company users and their attributes from database
   * @param {object} company Company database object
   * @returns {void}
   */
  getUsers = async company => {
    if (company.users) {
      const users = Object.keys(company.users).map(id => {
        return firebase.fetchUserData(id).then(user => {
          if (user) {
            user.role = company.users[user.uid].role;
            user.active = company.users[user.uid].active;
            return user;
          }
          return;
        });
      });
      // Returns once all promises are resolved
      return Promise.all(users);
    }
  };

  /**
   * Fetches user invitationsfrom database
   * Mobx: action
   * @returns {void}
   */
  getInvites = () => {
    this.setField('isInvitesLoading', true);
    this.setField('invites', []);
    // Loop through invitations and only show those relating to this company
    firebase.data.ref('userInvites').once(
      'value',
      snapshot => {
        if (snapshot.exists()) {
          // Find this company's invitations
          let invites = [];
          snapshot.forEach(snap => {
            const invite = snap.val();
            if (!invite.locationId && invite.companyId === this.company.id && !this.inviteExists(invite.id)) {
              invites.push(invite);
            }
          });
          this.setField('invites', invites);
          setTimeout(() => this.setField('isInvitesLoading', false), 100);
          this.stores.appStore.debug('CompanyStore.getInvites() [action] Invitations retrieved.');
        }
      },
      error => {
        this.setField('isInvitesLoading', false);
        this.stores.appStore.error('system', error);
      }
    );
  };

  /**
   * Updates user database record based on change in user table via edit row method.
   */
  updateUser = (newData, oldData) => {
    this.setField('isUsersLoading', true);
    return new Promise((resolve, reject) => {
      const index = this.users.indexOf(oldData);
      firebase
        .updateCompanyUser({
          companyId: this.company.id,
          newData: newData,
        })
        .then(() => {
          // Success
          this.setArrayField(this.users, index, newData);
          this.stores.appStore.debug('CompanyStore.updateUser() [action] Database updated successfully', newData.role);
          this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
            'User updated successfully.',
            'success',
            2750
          );
          this.setField('isUsersLoading', false);
          resolve();
        })
        .catch(error => {
          this.stores.appStore.error('system', error);
          this.setField('isUsersLoading', false);
          reject();
        });
    });
  };

  /**
   * Updates location database record based on change in location table via edit row method.
   */
  updateLocation = (newData, oldData) => {
    this.setField('isLocationsLoading', true);
    this.stores.appStore.debug('Company updateLocation() [action] Updating company location.');
    return new Promise((resolve, reject) => {
      const index = this.locations.indexOf(oldData);
      this.stores.appStore.debug('Company updateLocation() [action] Collecting changes...');

      // Compile Updates
      // Default active: true, and primary: false if values are undefined.
      let updates = {};
      updates['company/' + newData.companyId + '/locations/' + newData.id + '/active'] = newData.active;
      updates['company/' + newData.companyId + '/locations/' + newData.id + '/primary'] = newData.primary;
      updates['location/' + newData.id + '/name'] = newData.name;
      updates['location/' + newData.id + '/active'] = newData.active;
      updates['location/' + newData.id + '/primary'] = newData.primary;

      // Was Primary Flag updated?
      let currPrimaryVal;
      let currPrimaryIndex;

      if (newData.primary !== oldData.primary) {
        // Only one location can be primary, so find current primary and set to false
        this.locations.forEach(location => {
          if (location.primary) {
            // Add to update array
            updates['company/' + newData.companyId + '/locations/' + location.id + '/primary'] = false;
            updates['location/' + location.id + '/primary'] = false;

            // Get index of current primary location
            currPrimaryIndex = this.locations.indexOf(location);
            currPrimaryVal = this.locations[currPrimaryIndex];
            currPrimaryVal.primary = false;
          }
        });
      }
      this.stores.appStore.debug('Company updateLocation() [action] Changes collected, updating database.', updates);

      // Update database
      firebase.data
        .ref()
        .update(updates)
        .then(() => {
          // Success
          this.stores.appStore.debug('Company updateLocation() [action] Database updated successfully.');
          // Apply updates to local array for changes
          this.setArrayField(this.locations, index, newData);

          // Apply updates to local array for primary updates
          this.setArrayField(this.locations, currPrimaryIndex, currPrimaryVal);

          this.stores.appStore.debug(
            'CompanyStore.updateLocation() [action] Database updated successfully',
            newData.role
          );
          this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
            'Location updated successfully.',
            'success',
            2750
          );
          this.stores.appStore.debug('Company updateLocation() [action] Local store successfully.');
          this.setField('isLocationsLoading', false);
          resolve();
        })
        .catch(error => {
          this.stores.appStore.error('system', error);
          this.setField('isLocationsLoading', false);
          reject();
        });
    });
  };

  addLocation = newLocation => {
    this.setField('isLocationsLoading', true);
    this.stores.appStore.debug('Company addLocation() [action] Adding new company location.');
    return new Promise((resolve, reject) => {
      // New location Id
      const newId = uuid.v4();
      // Add new /location
      firebase.data
        .ref('location')
        .child(newId)
        .set({
          id: newId,
          active: true,
          companyId: this.companyId,
          name: newLocation.name,
          primary: false,
          address: newLocation.address,
        })
        .then(() => {
          this.stores.appStore.debug(`Company addLocation() [action] location/${newId} Added successfully.`);
          // Add new location to company.locations
          return firebase.data
            .ref('company')
            .child(this.companyId)
            .child('locations')
            .child(newId)
            .set({
              id: newId,
              active: true,
              name: newLocation.name,
            });
        })
        .then(() => {
          this.stores.appStore.debug(
            `Company addLocation() [action] company/${this.companyId}/locations/${newId} Added successfully.`
          );

          try {
            // Add new location to local store array
            const location = {
              id: newId,
              name: newLocation.name,
              address: newLocation.address,
              companyId: this.companyId,
              cityState: newLocation.address.city + ', ' + newLocation.address.state,
              active: true,
              primary: false,
            };

            // Check to make sure location doesn't already exist
            if (!this.locationExists(newId)) {
              this.locations.push(location);
              this.stores.appStore.debug(`Company addLocation() [action] Local store value updated.`);

              // Database updated successfully
              this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
                'Location added successfully.',
                'success',
                2750
              );
            }
          } catch (error) {
            // Error adding to local array
            this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
              'Your new address was added successfully, but your browser prevented us from updating the page view, please refresh your page.',
              'warning',
              2750
            );
          }

          // Complete
          setTimeout(() => this.setField('isLocationsLoading', false), 300);
          resolve();
        })
        .catch(error => {
          this.stores.appStore.error('system', error);
          this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
            'There was an error adding the new location, please try again.',
            'error',
            2750
          );
          this.setField('isLocationsLoading', false);
          reject(error);
        });
    });
  };

  /**
   * Fetches the badge history for entire company.
   * @function fetchBadgeHistory
   * @returns {void}
   */
  fetchBadgeHistory = async () => {
    this.setField('isBadgeHistoryLoading', true);
    this.stores.appStore.debug('CompanyStore.fetchBadgeHistory() [action] Fetching badge history...');
    firebase
      .getCompanyCheckins(Object.keys(this.company.locations))
      .then(badgeHistory => {
        this.setField('badgeHistory', badgeHistory);
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.debug('CompanyStore.fetchBadgeHistory() [action] Badge history retrieved.', badgeHistory);
      })
      .catch(error => {
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Resets store values to default
   */
  reset = () => {
    this.company = {};
    this.locations = observable([]);
    this.users = observable([]);
    this.invites = [];
    this.badgeHistory = observable([]);
    this.hasChanges = false;
    this.isLoading = true;
    this.isLocationsLoading = true;
    this.isUsersLoading = true;
    this.isInvitationsLoading = true;
    this.isBadgeHistoryLoading = true;
    this.isSendingInvite = false;
    this.stores.appStore.debug('CompanyStore.reset() [action] Company store has been reset.');
  };
}
decorate(CompanyStore, {
  company: observable,
  invites: observable,
  hasChanges: observable,
  isLoading: observable,
  isLocationsLoading: observable,
  isUsersLoading: observable,
  isInvitationsLoading: observable,
  isBadgeHistoryLoading: observable,
  isSendingInvite: observable,
  companyId: computed,
  isAdmin: computed,
  handleNameChange: action,
  handleAddressChange: action,
  handleRemoveInvite: action,
  handleSendInvite: action,
  handleSaveChanges: action,
  setField: action,
  setArrayField: action,
  setLocations: action,
  getLocations: action,
  setUsers: action,
  getUsers: action,
  getInvites: action,
  updateUser: action,
  updateLocation: action,
  initStore: action,
  addLocation: action,
  reset: action,
});

export default CompanyStore;
